import {useContext, useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Redirect, Route, Switch, useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {OfficeContext} from '../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../contexts/snack-bar/types';
import {translations} from '../../../../../../i18n/translations';
import {PlaylistsRepository} from '../../../../../../repositories/playlists';
import PlaylistItemsProfessionalCaregiverOfficeScreen from './items/playlist-items.professional-caregiver.screen';
import PlaylistUsersProfessionalCaregiverOfficeScreen from './users/playlist-users.professional-caregiver.screen';
import PlaylistDetailsProfessionalCaregiverOfficeScreen from './details/playlist-details.professional-caregiver.screen';
import PlaylistItemProfessionalCaregiverOfficeScreen from '../playlist-item/playlist-item.professional-caregiver.screen';

enum PathExtensions {
  Details = 'details',
  Items = 'items',
  Users = 'users',
  Item = 'items/:itemId',
}

export default function PlaylistProfessionalCaregiverOfficeScreen({
  canWrite,
  onPlaylistChange,
}: {
  canWrite: boolean;
  onPlaylistChange: (id: string) => void;
}) {
  // Get params
  let {playlistId, entityId}: {playlistId?: string; entityId?: string} = useParams();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [sendAppId, setSendAppId] = useState(false);

  useEffect(() => {
    if (!sendAppId) {
      if (playlistId) onPlaylistChange(playlistId);
      else console.error('No playlist id found in the URL.');
      setSendAppId(true);
    }
  }, [playlistId, onPlaylistChange, sendAppId]);

  // Validate and get the playlist
  useEffect(() => {
    const getPlaylist = async () => {
      try {
        // Check ids
        if (!playlistId || !entityId) {
          // Error
          throw new Error('Empty playlist or entity id.');
        }

        // Get playlist
        // Note: Don't localize because we need the original model for the playlist form
        const result =
          await PlaylistsRepository.instance.getPlaylistByIdForEntityAsAffiliatedForEntity(
            entityId,
            playlistId,
            false
          );

        if (result) {
          // Set playlist
          officeContext.setPlaylistForm(result);
        } else {
          // Error
          throw new Error('Empty playlist.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get playlist.', error);

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.playlists.professional_caregiver.edit.errors.playlist_does_not_exist_error
          ),
        });

        // Redirect
        history.push(`${url.replace(`/${playlistId}`, '')}`);
      }
    };

    // Get playlist
    getPlaylist();

    // Clean up
    return () => {
      // Clear playlist
      officeContext.clearPlaylistForm();

      // Log
      console.debug('🧹 Playlist form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);

  return (
    <Switch>
      {/* Playlist items */}
      <Route exact path={`${path}/${PathExtensions.Details}`}>
        <PlaylistDetailsProfessionalCaregiverOfficeScreen canWrite={false} />
      </Route>
      {/* Playlist items */}
      <Route exact path={`${path}/${PathExtensions.Items}`}>
        <PlaylistItemsProfessionalCaregiverOfficeScreen canWrite={canWrite} />
      </Route>
      {/* Playlist users */}
      <Route exact path={`${path}/${PathExtensions.Users}`}>
        <PlaylistUsersProfessionalCaregiverOfficeScreen canWrite={canWrite} />
      </Route>
      {/* Playlist item - Not exact, because we will have /items/:itemId/foobar */}
      <Route path={`${path}/${PathExtensions.Item}`}>
        <PlaylistItemProfessionalCaregiverOfficeScreen canWrite={canWrite} />
      </Route>

      {/** Default route */}
      <Route path={path}>
        <Redirect to={`${url}/${PathExtensions.Items}`} />
      </Route>
    </Switch>
  );
}
